'use client';

import * as React from 'react';
import { cn } from '@shared/utils';
import { withPlatformComponents, WithPlatformComponents } from '../../platform';

/**
 * @group Components
 * 
 * @summary
 * <blockquote>Universal Logo component that renders using platform-appropriate primitives.</blockquote>
 * 
 * @example
 * ```typescript
 * <Logo size={32} className="text-brand" />
 * ```
 * @internal
 */
interface LogoProps {
  size?: number;
  className?: string;
  color?: string;
}

const LogoComponent: React.FC<LogoProps & WithPlatformComponents> = ({ 
  size = 48, 
  color = "#084349",
  className, 
  components 
}) => {
  const { Svg, Path } = components;
  // Cropped viewBox to reduce whitespace while including all content
  // Content spans approximately X: 310-1270, Y: 240-730
  const viewBoxX = 300;
  const viewBoxY = 200;
  const viewBoxWidth = 1040;
  const viewBoxHeight = 550;
  const aspectRatio = viewBoxWidth / viewBoxHeight;
  
  const width = size * aspectRatio;
  const height = size;

  return (
    <Svg
      width={width}
      height={height}
      viewBox={`${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}`}
      xmlns="http://www.w3.org/2000/svg"
      fill={color}
      className={cn("inline-block text-teal-700 dark:text-teal-600", className)}
      style={{ verticalAlign: 'middle' }}
      preserveAspectRatio="xMidYMid meet"
    >
        <Path d="M312.77,726.68c-6.18-1.38-7.16-6.69-7.08-12.13-.07-81.77-.02-201.65-.03-288.08-.01-19.42.01-37.39,0-56.06.35-10.37-2.16-21.12,8.89-21.76,32.01-2.09,68.47-.33,100.26-.88,43.8-.01,91.1.56,129.42,24.26,36.66,23.92,53.93,72.21,36.54,113.57-11.46,31.29-35.38,65.4-63.57,81.22-29.93,16.96-60.79-4.61-69.04-34.63-2.04-8.94,3.97-13.61,11.97-15.61,7.95-2.34,19.71-4.09,28.19-10.33,21.34-15.65,21.81-48.76,12.7-71.68-8.83-22.97-28.72-35.76-53.82-41.06-15.06-3.23-34.72-4.55-46.45-3.8-13.8.72-19.42,5.06-18.42,19.76,0,40.81,0,81.95,0,126.12-.4,56.53.78,136.22-.44,182.95-.8,8.22-9.92,8.97-16.66,9.52-15.36.43-36.27,1.75-52.2-1.28l-.27-.08Z" transform="translate(0,-74.54)" />
        <Path d="M787.66,582.14c-4.59-2.35-3.75-7.95-3.99-12.57-.02-26.74,0-98.15,0-128.22,1.23-24.72-4.07-57.1-31.62-65.34-18.8-6.94-45.28,1.39-58.31,13.1-6.13,4.78-8.74,12.02-8.48,19.66-.32,43.3.52,138.15-.35,168.17-.58,6.65-8.02,6.57-13.27,6.91-9.08.31-23.9.22-32.98.09-3.86-.02-11.82-.13-12.99-3.06-1.51-43.9-.41-309.38-.4-356.33.42-2.88,2.65-4.16,9.7-5.34,4.69-.78,11.54-1.57,18.7-2.37,7.75-.8,15.43-1.45,20.57-1.14,3.74.22,6.31.97,7.91,2.53,3.65,4.09,2.73,10.82,3.06,16.4.05,26.19-.05,82.44.05,107.15.27,3.84-.17,9.21,3.07,10.99,4.81,2,22.09-11.44,30.7-14.41,24.63-11.18,56.56-13.37,81.23-2.95,24.73,9.54,40.47,34.77,43.42,60.28,1.96,16.04.85,26.41,1.13,43.98-.84,45.72,1.61,108.74-.94,139.39-2.99,5.35-8.01,4.21-15.44,4.87-11.09-.23-30.77,1.43-40.57-1.71l-.21-.1Z" transform="translate(0,70)" />
        <Path d="M920.82,283.95c-12.56-.41-25.74-6.69-31.32-18.12-7.47-15.21-3.68-35.02,13.49-44.08,25.6-13.02,60.01.22,55.67,32.52-2.09,18.81-18.04,30.56-37.3,29.71l-.54-.02ZM896.43,581.82c-3.29-.98-4.18-3.59-4.31-6.7-.22-8.37-.04-25.96-.1-48.14,0-54.66,0-120.38,0-176.83.34-8.96-2.03-17.81,7.13-19.47,14.2-2.93,33.34-5.51,47.13-3.86,5.69,1.31,5.7,5.41,5.89,12.73-.67,44.89,1.35,202.98-.76,239.07-2.97,6.12-15.6,4.03-25.53,4.62-8.33-.04-23.9.37-29.23-1.34l-.22-.07Z" transform="translate(0,70)" />
        <Path d="M1003.98,583.04c-3.58-.69-4.49-2.93-4.48-6.1-.08-10.38-.01-35.98-.03-68.99-.01-67.33,0-237.9.01-279.69.05-2.14.07-4.16,1.45-5.7,2.09-2.27,8.74-3.36,12.59-3.88,8.45-1.23,21.14-2.58,30.08-2.94,7.5.11,12.4-1.02,15.19,3.57,1.65,83.17,1.51,312.69.03,361.42-1.37,2.9-9.4,3.1-13.35,3.2-12.69-.12-28.75.96-41.25-.83l-.22-.05Z" transform="translate(0,70)" />
        <Path d="M1260.79,586.02l-3.48-2.49-3.48-30.33h-2.49c-27.35,22.37-45.74,33.81-78.06,33.81-39.78,0-71.1-27.34-71.1-65.63,0-58.17,49.72-83.03,145.18-100.93v-11.44c0-29.83-22.87-45.74-50.22-45.74s-48.23,10.94-63.14,22.87l-18.4-35.3c21.38-14.91,51.21-28.84,94.47-28.84,67.62,0,95.46,32.81,95.46,90.99v111.37l7.96,55.19-52.7,6.46ZM1247.36,454.27c-66.62,9.45-85.02,30.83-85.02,53.7s15.41,36.79,38.78,36.79c16.91,0,34.31-8.95,46.24-21.88v-68.61Z" transform="translate(0,70)" />
    </Svg>
  );
};

export const LogoText = withPlatformComponents(LogoComponent);
